import {
  ConfigurationLevel,
  ConfigurationValues,
  getDefaultConfiguration,
  normalizeConfigurations,
  NormalizeConfigurationsContext,
  resolveSystem,
  SashbarConfiguration,
} from '@cutr/constants/windows/windows.ts';
import {
  BasicValue,
  ConfigurationKey,
  CoversConfigurationOptions,
  CustomRequestsOptions,
  FittingConfigurationOptions,
  GeometryConfigurationOptions,
  StandardConfigurationOptions,
} from '@cutr/constants/windows/windows-configurations.ts';
import {
  createContext,
  Dispatch,
  ReactNode,
  useCallback,
  useContext,
} from 'react';

import { useWindowStore } from '../../api/windowStore';

export type ConfigurationsAction =
  | {
      type: 'CUSTOMIZE_CONFIGURATIONS';
      toDelete: ConfigurationKey[];
      toAdd: ConfigurationKey[];
    }
  | {
      type: 'CUSTOMIZE_POSITION_CONFIGURATIONS';
      orderConfigurations: ConfigurationValues;
      toDelete: ConfigurationKey[];
      toAdd: ConfigurationKey[];
    }
  | {
      type: 'SET_SYSTEM';
      value: StandardConfigurationOptions['system']['options'][number];
    }
  | {
      type: 'SET_THICKNESS';
      value: StandardConfigurationOptions['thickness']['options'][number];
    }
  | {
      type: 'SET_WOOD_TYPE';
      value: StandardConfigurationOptions['woodType']['options'][number];
    }
  | {
      type: 'SET_GLASS_TYPE';
      value: StandardConfigurationOptions['glassType']['options'][number];
    }
  | {
      type: 'SET_PAINT_COLOR_INSIDE';
      value: StandardConfigurationOptions['paintColorInside']['options'][number];
    }
  | {
      type: 'SET_PAINT_COLOR_OUTSIDE';
      value: StandardConfigurationOptions['paintColorOutside']['options'][number];
    }
  | {
      type: 'SET_ALU_CLADDING_COLOR';
      value: StandardConfigurationOptions['aluCladdingColor']['options'][number];
    }
  | {
      type: 'SET_SEALANT_INNER_COLOR';
      value: StandardConfigurationOptions['sealantInnerColor']['options'][number];
    }
  | {
      type: 'SET_SEALANT_OUTER_COLOR';
      value: StandardConfigurationOptions['sealantOuterColor']['options'][number];
    }
  | {
      type: 'SET_SEAL_OPTIONS';
      value: StandardConfigurationOptions['sealOptions']['options'][number];
    }
  | { type: 'SET_HANDLE'; value: BasicValue }
  | {
      type: 'SET_HARDWARE';
      value: StandardConfigurationOptions['hardware']['options'][number];
    }
  | {
      type: 'SET_CUSTOM_REQUESTS';
      value: Partial<CustomRequestsOptions>;
    }
  | {
      type: 'SET_WINDOW_SILLS';
      value: GeometryConfigurationOptions['windowSills']['options'][number];
    }
  | {
      type: 'SET_WINDOW_SILL_DECORATIVE_PROFILE';
      value: GeometryConfigurationOptions['windowSillDecorativeProfile']['options'][number];
    }
  | {
      type: 'SET_RAIN_GUARD';
      value: CoversConfigurationOptions['rainGuardColor']['options'][number];
    }
  | {
      type: 'SET_SASH_COVER';
      value: CoversConfigurationOptions['sashCoverColor']['options'][number];
    }
  | {
      type: 'SET_WEATHER_RAIL';
      value: CoversConfigurationOptions['weatherRail']['options'][number];
    }
  | {
      type: 'SET_COVER_STRIPS';
      value: {
        insideTop: FittingConfigurationOptions['coverStrips']['insideTop']['options'][number];
        insideRight: FittingConfigurationOptions['coverStrips']['insideRight']['options'][number];
        insideBottom: FittingConfigurationOptions['coverStrips']['insideBottom']['options'][number];
        insideLeft: FittingConfigurationOptions['coverStrips']['insideLeft']['options'][number];
        outsideTop: FittingConfigurationOptions['coverStrips']['outsideTop']['options'][number];
        outsideRight: FittingConfigurationOptions['coverStrips']['outsideRight']['options'][number];
        outsideBottom: FittingConfigurationOptions['coverStrips']['outsideBottom']['options'][number];
        outsideLeft: FittingConfigurationOptions['coverStrips']['outsideLeft']['options'][number];
      };
    }
  | {
      type: 'SET_PLASTERING_STRIPS';
      value: {
        insideTop: FittingConfigurationOptions['plasteringStrips']['insideTop']['options'][number];
        insideRight: FittingConfigurationOptions['plasteringStrips']['insideRight']['options'][number];
        insideBottom: FittingConfigurationOptions['plasteringStrips']['insideBottom']['options'][number];
        insideLeft: FittingConfigurationOptions['plasteringStrips']['insideLeft']['options'][number];
        outsideTop: FittingConfigurationOptions['plasteringStrips']['outsideTop']['options'][number];
        outsideRight: FittingConfigurationOptions['plasteringStrips']['outsideRight']['options'][number];
        outsideBottom: FittingConfigurationOptions['plasteringStrips']['outsideBottom']['options'][number];
        outsideLeft: FittingConfigurationOptions['plasteringStrips']['outsideLeft']['options'][number];
      };
    }
  | {
      type: 'SET_SASHBARS';
      value: SashbarConfiguration[];
    }
  | {
      type: 'SET_FRAME_BEAM_WIDTH';
      value: {
        top: GeometryConfigurationOptions['frameBeamWidth']['options'][number];
        left: GeometryConfigurationOptions['frameBeamWidth']['options'][number];
        right: GeometryConfigurationOptions['frameBeamWidth']['options'][number];
        bottom: GeometryConfigurationOptions['frameBeamWidth']['options'][number];
      };
    }
  | {
      type: 'SET_SASH_BEAM_WIDTH';
      value: {
        top: GeometryConfigurationOptions['sashBeamWidth']['options'][number];
        left: GeometryConfigurationOptions['sashBeamWidth']['options'][number];
        right: GeometryConfigurationOptions['sashBeamWidth']['options'][number];
        bottom: GeometryConfigurationOptions['sashBeamWidth']['options'][number];
      };
    };

export type ConfigurationsReducerContext = Omit<
  NormalizeConfigurationsContext,
  'systemChanged'
>;

export type ConfigurationsContextType = {
  configurations: ConfigurationValues;
  dispatch: Dispatch<ConfigurationsAction>;
};

const ConfigurationsContext = createContext<
  ConfigurationsContextType | undefined
>(undefined);

// Assigning `state[key] = value` with a union-typed key loses the
// key↔value correlation; routing it through a single generic K keeps it.
const setConfiguration = <K extends ConfigurationKey>(
  target: Partial<ConfigurationValues>,
  key: K,
  value: ConfigurationValues[K]
) => {
  target[key] = value;
};

export function configurationsReducer<T extends ConfigurationValues>(
  state: T,
  action: ConfigurationsAction,
  ctx: ConfigurationsReducerContext = {}
): T {
  let newState: T;
  switch (action.type) {
    case 'SET_SYSTEM':
      newState = { ...state, system: action.value };
      break;
    case 'SET_THICKNESS':
      newState = { ...state, thickness: action.value };
      break;
    case 'SET_WOOD_TYPE':
      newState = { ...state, woodType: action.value };
      break;
    case 'SET_GLASS_TYPE':
      newState = { ...state, glassType: action.value };
      break;
    case 'SET_PAINT_COLOR_INSIDE':
      newState = { ...state, paintColorInside: action.value };
      break;
    case 'SET_PAINT_COLOR_OUTSIDE':
      newState = { ...state, paintColorOutside: action.value };
      break;
    case 'SET_ALU_CLADDING_COLOR':
      newState = { ...state, aluCladdingColor: action.value };
      break;
    case 'SET_SEALANT_INNER_COLOR':
      newState = { ...state, sealantInnerColor: action.value };
      break;
    case 'SET_SEALANT_OUTER_COLOR':
      newState = { ...state, sealantOuterColor: action.value };
      break;
    case 'SET_HANDLE':
      newState = { ...state, handle: action.value };
      break;
    case 'SET_HARDWARE':
      newState = { ...state, hardware: action.value };
      break;
    case 'SET_SEAL_OPTIONS':
      newState = { ...state, sealOptions: action.value };
      break;
    case 'SET_CUSTOM_REQUESTS':
      newState = {
        ...state,
        customRequests: { ...state.customRequests, ...action.value },
      };
      break;
    case 'SET_WINDOW_SILLS':
      newState = { ...state, windowSills: action.value };
      break;
    case 'SET_WINDOW_SILL_DECORATIVE_PROFILE':
      newState = { ...state, windowSillDecorativeProfile: action.value };
      break;
    case 'SET_RAIN_GUARD':
      newState = { ...state, rainGuardColor: action.value };
      break;
    case 'SET_SASH_COVER':
      newState = { ...state, sashCoverColor: action.value };
      break;
    case 'SET_WEATHER_RAIL':
      newState = { ...state, weatherRail: action.value };
      break;
    case 'CUSTOMIZE_CONFIGURATIONS':
      newState = { ...state };
      for (const key of action.toDelete) {
        delete newState[key];
      }
      for (const key of action.toAdd) {
        const value = getDefaultConfiguration(
          key,
          newState.system ?? ctx.system
        );
        if (value !== undefined) setConfiguration(newState, key, value);
      }
      break;
    case 'CUSTOMIZE_POSITION_CONFIGURATIONS':
      newState = { ...state };
      for (const key of action.toDelete) {
        delete newState[key];
      }
      for (const key of action.toAdd) {
        const system = newState.system ?? ctx.system;
        const inherited = normalizeConfigurations(
          { [key]: action.orderConfigurations[key] },
          {
            ...ctx,
            system,
            thickness: newState.thickness ?? ctx.thickness,
            level: 'position',
          }
        )[key];
        const value = inherited || getDefaultConfiguration(key, system);
        if (value !== undefined) setConfiguration(newState, key, value);
      }
      break;
    case 'SET_COVER_STRIPS':
      newState = { ...state, coverStrips: action.value };
      break;
    case 'SET_PLASTERING_STRIPS':
      newState = { ...state, plasteringStrips: action.value };
      break;
    case 'SET_SASHBARS':
      newState = {
        ...state,
        sashbars: [...action.value],
      };
      break;
    case 'SET_FRAME_BEAM_WIDTH':
      newState = { ...state, frameBeamWidth: action.value };
      break;
    case 'SET_SASH_BEAM_WIDTH':
      newState = { ...state, sashBeamWidth: action.value };
      break;
  }

  return normalizeConfigurations(newState, {
    ...ctx,
    systemChanged: action.type === 'SET_SYSTEM',
  });
}

export const configurationsReducerContext = (
  inherited: Partial<ConfigurationValues> | undefined,
  level: ConfigurationLevel
): ConfigurationsReducerContext => ({
  system: inherited?.system,
  thickness: inherited?.thickness,
  inherited,
  level,
});

export const ConfigurationsProvider = <T extends ConfigurationValues>({
  configurations,
  inheritedConfigurations,
  level = 'order',
  onConfigurationChange,
  children,
}: {
  configurations: T;
  inheritedConfigurations?: Partial<ConfigurationValues>;
  level?: ConfigurationLevel;
  onConfigurationChange?: (configurations: T) => void;
  children: ReactNode;
}) => {
  const dispatch = useCallback(
    (action: ConfigurationsAction) => {
      onConfigurationChange?.(
        configurationsReducer(
          configurations,
          action,
          configurationsReducerContext(inheritedConfigurations, level)
        )
      );
    },
    [configurations, inheritedConfigurations, level, onConfigurationChange]
  );

  return (
    <ConfigurationsContext.Provider value={{ configurations, dispatch }}>
      {children}
    </ConfigurationsContext.Provider>
  );
};

export function useConfigurations() {
  const context = useContext(ConfigurationsContext);
  if (!context) {
    throw new Error(
      'useConfigurations must be used within a ConfigurationsProvider'
    );
  }
  return context;
}

// Position configurations only hold their own overrides, so `system` is
// usually unset there; resolve it against the order's system.
export function useResolvedSystem(): ConfigurationValues['system'] {
  const { configurations } = useConfigurations();
  const { configurations: orderConfigurations } = useWindowStore();
  return resolveSystem({
    orderConfigurations,
    positionConfigurations: configurations,
  })!;
}
